import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { useReactFlow } from '@xyflow/react'
import {
  Bold,
  ChevronDown,
  ChevronUp,
  Italic,
  Link as LinkIcon,
  List,
  Palette,
  Strikethrough,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type ReactQuill from 'react-quill-new'
import { useFlowStore } from '../../store'
import { ColorPicker } from './color-picker'
import { sizeOptions } from './note.constants'

interface NoteToolbarProps {
  nodeId: string
  isEditing: boolean
  currentBackgroundColor: string
  textSize: string
  onBackgroundColorChange: (color: string) => void
  onSizeChange: (value: string | string[]) => void
  onInsertLink: (url: string) => void
  quillRef: React.RefObject<ReactQuill>
}

export interface ActiveFormats {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  list: boolean
}

const toActiveFormats = (formats: Record<string, unknown>): ActiveFormats => ({
  bold: !!formats.bold,
  italic: !!formats.italic,
  underline: !!formats.underline,
  strike: !!formats.strike,
  list: !!formats.list,
})

export const NoteToolbar = ({
  nodeId,
  isEditing,
  currentBackgroundColor,
  textSize,
  onBackgroundColorChange,
  onSizeChange,
  onInsertLink,
  quillRef,
}: NoteToolbarProps) => {
  const flowInstance = useReactFlow()
  const selectedNode = useFlowStore((state) => state.selectedNodeID)
  const [linkUrl, setLinkUrl] = useState('')
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false)
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    list: false,
  })

  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    const handleEditorChange = () => {
      // getSelection() without focus: null when the editor is blurred (e.g. link dialog open);
      // getFormat() with no range would call focus() and throw on a null selection.
      const range = editor?.getSelection()
      if (editor && range) setActiveFormats(toActiveFormats(editor.getFormat(range)))
    }

    editor?.on('editor-change', handleEditorChange)
    return () => {
      editor?.off('editor-change', handleEditorChange)
    }
  }, [quillRef])

  const onFormatClick = (format: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const editor = quillRef.current?.getEditor()
    if (editor) {
      editor.focus()
      const current = editor.getFormat()
      // Quill 2 lists need a named value; `true` renders no bullet marker
      editor.format(
        format,
        format === 'list' ? (current.list ? false : 'bullet') : !current[format],
      )
      setActiveFormats(toActiveFormats(editor.getFormat()))
      requestAnimationFrame(() => {
        editor.focus()
      })
    }
  }

  const handleBringToFront = () => {
    const nodes = flowInstance.getNodes()
    const noteNodes = nodes.filter((node) => node.type === 'note')
    const maxZIndex = Math.max(...noteNodes.map((node) => node.zIndex || 0))
    flowInstance.updateNode(nodeId, { zIndex: maxZIndex + 1 })
  }

  const handleSendToBack = () => {
    const nodes = flowInstance.getNodes()
    const noteNodes = nodes.filter((node) => node.type === 'note')
    const minZIndex = Math.min(...noteNodes.map((node) => node.zIndex || 0))
    flowInstance.updateNode(nodeId, { zIndex: minZIndex - 1 })
  }

  return (
    <div
      className={cn(
        'note-toolbar',
        'border-st2 border p-1 hidden items-center space-x-1 absolute top-[-50px] shadow-sm rounded-md bg-background left-1/2 -translate-x-1/2',
        selectedNode === nodeId && isEditing && 'flex',
      )}
    >
      <DropdownMenu>
        <Tooltip content="Change note background color">
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded hover:bg-muted"
              aria-label="Node background color"
              data-testid="button-bg-color"
            >
              <Palette size={16} />
            </button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent
          sideOffset={5}
          align="center"
          className="p-2 bg-popover border border-st2 shadow-md rounded-md"
        >
          <DropdownMenuItem className="!bg-background">
            <ColorPicker
              currentBackgroundColor={currentBackgroundColor}
              onColorChange={onBackgroundColorChange}
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="h-4 w-px bg-st2 mx-1" />
      <Tooltip content="Bold">
        <button
          type="button"
          onClick={(e) => {
            onFormatClick('bold', e)
          }}
          className={cn(
            'p-1 rounded hover:bg-muted',
            activeFormats.bold &&
              '!bg-primary/60 text-background hover:!bg-primary/30 !hover:text-background',
          )}
          aria-label="Bold"
          data-testid="button-bold"
        >
          <Bold size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Italic">
        <button
          type="button"
          onClick={(e) => {
            onFormatClick('italic', e)
          }}
          className={cn(
            'p-1 rounded hover:bg-muted',
            activeFormats.italic &&
              '!bg-primary/60 text-background hover:!bg-primary/30 !hover:text-background',
          )}
          aria-label="Italic"
          data-testid="button-italic"
        >
          <Italic size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Strikethrough">
        <button
          type="button"
          onClick={(e) => {
            onFormatClick('strike', e)
          }}
          className={cn(
            'p-1 rounded hover:bg-muted',
            activeFormats.strike &&
              '!bg-primary/60 text-background hover:!bg-primary/30 !hover:text-background',
          )}
          aria-label="Strikethrough"
          data-testid="button-strikethrough"
        >
          <Strikethrough size={16} />
        </button>
      </Tooltip>
      <div className="h-4 w-px bg-st2 mx-1" />
      <Tooltip content="Bullet list">
        <button
          type="button"
          onClick={(e) => {
            onFormatClick('list', e)
          }}
          className={cn(
            'p-1 rounded hover:bg-muted',
            activeFormats.list &&
              '!bg-primary/60 text-background hover:!bg-primary/30 !hover:text-background',
          )}
          aria-label="Bullet list"
          data-testid="button-bullet-list"
        >
          <List size={16} />
        </button>
      </Tooltip>
      <div className="h-4 w-px bg-st2 mx-1" />
      <DropdownMenu open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <Tooltip content="Insert link">
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded hover:bg-muted"
              aria-label="Insert link"
              data-testid="button-link-trigger"
            >
              <LinkIcon size={16} />
            </button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent
          sideOffset={8}
          align="center"
          className="p-2 flex flex-col space-y-2 w-64 bg-popover border-st2 shadow-md rounded-md"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Input
            placeholder="Enter URL"
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            data-testid="input-link-url"
            className="text-sm"
          />
          <div className="flex justify-end space-x-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (linkUrl.trim()) {
                  onInsertLink(linkUrl.trim())
                  setLinkUrl('')
                  setIsLinkDialogOpen(false)
                }
              }}
              disabled={!linkUrl.trim()}
              data-testid="button-save-link"
            >
              Save
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="h-4 w-px bg-st2 mx-1" />
      <div className="w-auto">
        <Tooltip content="Text size">
          <div>
            <Select
              options={sizeOptions}
              value={textSize}
              onChange={onSizeChange}
              placeholder="Normal"
              variant="ghost"
              size="sm"
              className="!w-full !flex !justify-start"
              searchable={false}
              data-testid="select-size"
            />
          </div>
        </Tooltip>
      </div>
      <div className="h-4 w-px bg-st2 mx-1" />
      <Tooltip content="Bring to front">
        <button
          type="button"
          onClick={handleBringToFront}
          className="p-1 rounded hover:bg-muted"
          aria-label="Bring to front"
          data-testid="button-bring-front"
        >
          <ChevronUp size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Send to back">
        <button
          type="button"
          onClick={handleSendToBack}
          className="p-1 rounded hover:bg-muted"
          aria-label="Send to back"
          data-testid="button-send-back"
        >
          <ChevronDown size={16} />
        </button>
      </Tooltip>
      <div className="h-4 w-px bg-st2 mx-1" />
      <Tooltip content="Delete note">
        <button
          type="button"
          className="p-1 rounded hover:bg-destructive/20"
          aria-label="Delete note"
          data-testid="button-delete-note"
          onClick={(e) => {
            e.stopPropagation()
            flowInstance.deleteElements({ nodes: [{ id: nodeId }] })
          }}
        >
          <Trash2 size={16} className="text-destructive" />
        </button>
      </Tooltip>
    </div>
  )
}
